import { formatRecordedMoney } from "utils/recordedMoney";
import useCreatePurchaseOrder from "api/OrderResource/useCreatePurchaseOrder";
import { ButtonLoader } from "components";
import { Form, Formik } from "formik";
import React, { useEffect, useRef } from "react";
import { activeCurrencies } from "utils";
import useCurrencyStore from "store/currencyStore";
import { FaTicketAlt } from "react-icons/fa";
import { FiLock, FiAlertCircle, FiHelpCircle, FiTag, FiCheckSquare } from "react-icons/fi";
import * as Yup from "yup";

const VoucherPayment = ({ grandTotal, books, setPaymentIsSuccessful, addressId, recipientUserId }) => {
	const { currency } = useCurrencyStore();
	const voucherInputRef = useRef(null);

	// Auto-focus the voucher input when component mounts
	useEffect(() => {
		if (voucherInputRef.current) {
			voucherInputRef.current.focus();
		}
	}, []);

	const initialValues = {
		voucher_number: "",
	};

	const validateVoucher = Yup.object().shape({
		voucher_number: Yup.string()
			.required("Voucher code is required")
			.min(6, "Voucher code must be at least 6 characters"),
	});

	const { mutateAsync, isLoading, error } = useCreatePurchaseOrder();

	const handleSubmit = async (data) => {
		await mutateAsync({
			payment_method: "Voucher",
			payment_type: "Purchase",
			subtotal: 0,
			grand_charges: 0,
			books: books,
			grand_total: currency === activeCurrencies.USD.code ? grandTotal : 0,
			grand_total_zwl: currency === activeCurrencies.ZWL.code ? grandTotal : 0,
			reference: Date.now(),
			status: "Completed",
			initiated_from: "cart",
			voucher_number: data.voucher_number,
			currency: currency,
			total_amount: grandTotal,
			address_id: addressId,
			...(recipientUserId && { recipient_user_id: recipientUserId }),
		});
		setPaymentIsSuccessful(true);
	};

	return (
		<div>
			{isLoading ? (
				<div className="flex flex-col gap-4 items-center justify-center py-8">
					<div className="w-16 h-16 rounded-full bg-purple-50 flex items-center justify-center">
						<ButtonLoader size={40} />
					</div>
					<div className="text-center">
						<p className="font-medium text-gray-900">Applying voucher...</p>
						<p className="text-sm text-gray-500 mt-1">Please wait while we verify your code</p>
					</div>
				</div>
			) : (
				<Formik
					initialValues={initialValues}
					validationSchema={validateVoucher}
					onSubmit={handleSubmit}
				>
					{(formik) => (
						<Form className="space-y-4">
							{/* Voucher Header */}
							<div className="flex items-center gap-3 pb-3 border-b border-gray-100">
								<div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
									<FaTicketAlt className="w-5 h-5 text-purple-600" />
								</div>
								<div>
									<p className="font-semibold text-gray-900">Voucher Payment</p>
									<p className="text-sm text-gray-500">
										Amount: <span className="font-bold text-akgreener">{formatRecordedMoney(grandTotal, currency)}</span>
									</p>
								</div>
							</div>

							{/* Input Section */}
							<div className="space-y-2">
								<label className="block text-sm font-medium text-gray-700">
									Enter Voucher Code
								</label>
								<div className="relative">
									<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
										<FiTag className="h-5 w-5 text-gray-400" />
									</div>
									<input
										ref={voucherInputRef}
										type="text"
										name="voucher_number"
										value={formik.values.voucher_number}
										onChange={(e) => formik.setFieldValue('voucher_number', e.target.value.toUpperCase())}
										onBlur={formik.handleBlur}
										placeholder="e.g., TKRpiLHXXy"
										className={`w-full pl-10 pr-4 py-3 border rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-200 focus:border-purple-400 transition-all uppercase tracking-wider font-mono ${
											(formik.errors.voucher_number && formik.touched.voucher_number) || error
												? 'border-red-300 bg-red-50'
												: 'border-gray-200'
										}`}
									/>
								</div>
								
								{/* Error Messages */}
								{formik.errors.voucher_number && formik.touched.voucher_number && (
									<div className="flex items-center gap-1 text-red-500 text-sm">
										<FiAlertCircle className="w-4 h-4" />
										{formik.errors.voucher_number}
									</div>
								)}
								{error && (
									<div className="flex items-center gap-1 text-red-500 text-sm bg-red-50 p-2 rounded-lg">
										<FiAlertCircle className="w-4 h-4 flex-shrink-0" />
										<span>{error?.response?.data?.message || "Invalid voucher code. Please try again."}</span>
									</div>
								)}
							</div>

							{/* How it works */}
							<div className="bg-purple-50 border border-purple-100 rounded-lg p-3 mb-4">
								<div className="flex items-start gap-2">
									<FiCheckSquare className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
									<div className="text-sm text-gray-700">
										<p className="font-medium mb-1">How it works:</p>
										<ol className="list-decimal list-inside space-y-1 text-gray-600">
											<li>Enter your voucher code above</li>
											<li>Click Apply to redeem the voucher</li>
											<li>Your payment will be processed instantly</li>
										</ol>
									</div>
								</div>
							</div>

							{/* Help Text */}
							<div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
								<FiHelpCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
								<div>
									<p className="font-medium text-gray-600">Where to find your voucher code?</p>
									<p className="mt-0.5">Check your email or the physical voucher card for the 10-character code.</p>
								</div>
							</div>

							{/* Submit Button */}
							<button
								type="submit"
								disabled={isLoading || !formik.values.voucher_number}
								className="w-full bg-gradient-to-r from-akgreen to-akgreener text-white font-semibold py-3.5 px-6 rounded-xl hover:from-akgreener hover:to-akgreenest transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
							>
								<FiLock className="w-4 h-4" />
								Apply Voucher & Pay
							</button>

							{/* Security Note */}
							<p className="text-center text-xs text-gray-500 flex items-center justify-center gap-1">
								<FiLock className="w-3 h-3" />
								Secure voucher redemption
							</p>
						</Form>
					)}
				</Formik>
			)}
		</div>
	);
};

export default VoucherPayment;
